import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { getApi } from '../api';
import getAccountShipmentRoutesBatch from '../getAccountShipmentRoutesBatch';
import getAccountShipmentV2 from '../getAccountShipmentV2';
import type { PostedShipmentStopRow } from './postAccountShipmentStops';

export type PutAccountShipmentStopsVariables = {
  accountId: number;
  shipmentId: number;
  payload: { stops: Record<string, unknown>[] };
  skipInvalidation?: boolean;
};

/**
 * PUT `/api/v2/accounts/{account}/shipments/{shipment}/stops` — sync nested stops when rows already exist.
 */
export default function usePutAccountShipmentStops() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      shipmentId,
      payload,
    }: PutAccountShipmentStopsVariables) => {
      const { api, baseURL } = getApi();
      const res = await api.put(
        `/api/v2/accounts/${accountId}/shipments/${shipmentId}/stops`,
        payload,
        { baseURL },
      );
      return (res.data?.data ?? res.data) as PostedShipmentStopRow[];
    },
    onSuccess: (_data, variables) => {
      if (variables.skipInvalidation) return;
      queryClient.invalidateQueries({ queryKey: ['account-shipments'] });
      queryClient.invalidateQueries({
        queryKey: getAccountShipmentV2.queryKey(
          variables.accountId,
          variables.shipmentId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: getAccountShipmentRoutesBatch.queryKey(
          variables.accountId,
          [variables.shipmentId],
        ),
      });
    },
  });
}
